import React from "react";
import { connect } from "react-redux";
import { Col, Form, Row } from "react-bootstrap";
import PropTypes from "prop-types";
import { NumericFormat as NumberFormat } from "react-number-format";

import ConditionalButton from "../../../layout/conditional-button";

import { get as getSetting, save as saveSetting } from "../../../actions/settings";
import { loading, send as pageInfo } from "../../../actions/page";
import { pack as sendAlert } from "../../../actions/alert";

const DEFAULT_LEAD_SETTINGS = {
  followUp: {
    enabled: true,
    requireOpenLeadFollowUp: true,
    calendarMirror: false,
    defaultAssignedTo: "assignedToUuid",
    consultation: {
      enabled: true,
      daysOut: 1,
      eventTypePrefix: "CN",
    },
    cadence: [
      { afterContacts: 0, daysOut: 1 },
      { afterContacts: 1, daysOut: 2 },
      { afterContacts: 2, daysOut: 3 },
      { afterContacts: 4, daysOut: 7 },
      { afterContacts: 6, daysOut: 14 },
    ],
  },
};

const clone = (value) => JSON.parse(JSON.stringify(value || {}));

const safeParseJson = (value, fallback = {}) => {
  try {
    return typeof value === "string" ? JSON.parse(value) : (value || fallback);
  } catch (_err) {
    return fallback;
  }
};

const normalizeCadence = (cadence) => (
  Array.isArray(cadence) ? cadence : DEFAULT_LEAD_SETTINGS.followUp.cadence
).map((item) => ({
  afterContacts: Number(item.afterContacts || 0),
  daysOut: Number(item.daysOut || 1),
})).sort((a, b) => a.afterContacts - b.afterContacts);

const mergeLeadSettings = (value) => {
  const configuredFollowUp = value?.followUp || value?.leadFollowUp || {};
  const followUp = {
    ...clone(DEFAULT_LEAD_SETTINGS.followUp),
    ...configuredFollowUp,
    consultation: {
      ...clone(DEFAULT_LEAD_SETTINGS.followUp.consultation),
      ...(configuredFollowUp.consultation || {}),
    },
  };

  followUp.cadence = normalizeCadence(followUp.cadence);
  followUp.consultation.daysOut = Number(followUp.consultation.daysOut || 1);

  return {
    followUp,
  };
};

class LeadSettings extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      setting: null,
      leads: clone(DEFAULT_LEAD_SETTINGS),
    };
  }

  componentDidMount = async () => {
    await this.props.pageInfo({ title: "Lead Settings", breadcrumb: "Lead" });
    await this.loadData();
  };

  loadData = async () => {
    this.props.loading(true);
    try {
      let setting = await this.props.getSetting({ authentic: true, name: "LEADS" });
      if (!setting?.uuid) setting = await this.props.getSetting({ authentic: true, name: "LEAD_CRM" });
      this.setState({
        setting,
        leads: mergeLeadSettings(safeParseJson(setting?.value, DEFAULT_LEAD_SETTINGS)),
      });
    } finally {
      this.props.loading(false);
    }
  };

  setFollowUp = (field, value) => {
    this.setState((prev) => ({
      leads: {
        ...prev.leads,
        followUp: {
          ...prev.leads.followUp,
          [field]: value,
        },
      },
    }));
  };

  onToggle = (field) => (e) => {
    this.setFollowUp(field, e.target.checked);
  };

  setConsultation = (field, value) => {
    this.setState((prev) => ({
      leads: {
        ...prev.leads,
        followUp: {
          ...prev.leads.followUp,
          consultation: {
            ...(prev.leads.followUp.consultation || {}),
            [field]: value,
          },
        },
      },
    }));
  };

  onConsultationToggle = (field) => (e) => {
    this.setConsultation(field, e.target.checked);
  };

  onCadenceChange = (index, field, value) => {
    this.setState((prev) => {
      const cadence = [...(prev.leads.followUp.cadence || [])];
      cadence[index] = {
        ...cadence[index],
        [field]: Number(value || 0),
      };

      return {
        leads: {
          ...prev.leads,
          followUp: {
            ...prev.leads.followUp,
            cadence,
          },
        },
      };
    });
  };

  onAddCadence = () => {
    this.setState((prev) => {
      const cadence = normalizeCadence(prev.leads.followUp.cadence || []);
      const last = cadence[cadence.length - 1] || { afterContacts: 0, daysOut: 1 };

      return {
        leads: {
          ...prev.leads,
          followUp: {
            ...prev.leads.followUp,
            cadence: [
              ...cadence,
              {
                afterContacts: Number(last.afterContacts || 0) + 1,
                daysOut: Number(last.daysOut || 1),
              },
            ],
          },
        },
      };
    });
  };

  onRemoveCadence = (index) => {
    this.setState((prev) => ({
      leads: {
        ...prev.leads,
        followUp: {
          ...prev.leads.followUp,
          cadence: (prev.leads.followUp.cadence || []).filter((_, i) => i !== index),
        },
      },
    }));
  };

  onSaveClick = async () => {
    const leads = mergeLeadSettings(this.state.leads);

    this.props.loading(true);
    try {
      const result = await this.props.saveSetting({
        uuid: this.state.setting?.uuid,
        name: "LEADS",
        category: "Services",
        description: "Lead follow-up policy and cadence settings",
        visibility: this.state.setting?.visibility ?? 3,
        priority: this.state.setting?.priority ?? 0,
        value: JSON.stringify(leads),
      });

      this.setState({ setting: result, leads });
      this.props.sendAlert(["Lead settings saved."], "success");
    } finally {
      this.props.loading(false);
    }
  };

  renderCadence = (item, index) => (
    <Col xs="12" key={`cadence_${index}`}>
      <Row className="align-items-center g-3 border-bottom pb-3 mb-3">
        <Col xs="12" md="5">
          <div className="top-label">
            <NumberFormat
              displayType="input"
              type="text"
              className="text-end form-control"
              thousandSeparator={false}
              allowNegative={false}
              value={item.afterContacts ?? 0}
              onValueChange={(e) => this.onCadenceChange(index, "afterContacts", e.floatValue)}
            />
            <Form.Label>AFTER CONTACTS</Form.Label>
          </div>
        </Col>
        <Col xs="12" md="5">
          <div className="top-label">
            <NumberFormat
              displayType="input"
              type="text"
              className="text-end form-control"
              thousandSeparator={false}
              allowNegative={false}
              value={item.daysOut ?? 1}
              onValueChange={(e) => this.onCadenceChange(index, "daysOut", e.floatValue)}
            />
            <Form.Label>DAYS OUT</Form.Label>
          </div>
        </Col>
        <Col xs="12" md="2" className="text-end">
          <button type="button" className="btn btn-link btn-sm p-0" onClick={() => this.onRemoveCadence(index)}>
            Remove
          </button>
        </Col>
      </Row>
    </Col>
  );

  render() {
    const followUp = this.state.leads.followUp || {};
    const consultation = followUp.consultation || {};

    return (
      <>
        <Col md="8" className="float-right text-end">
          <ConditionalButton
            enabledVariant="outline-primary"
            tooltip="Save Lead Settings"
            display
            enabled
            onEnabledClick={this.onSaveClick}
            icon="save"
          >
            Save
          </ConditionalButton>
        </Col>

        <Col xs="12">
          <Row className="g-3">
            <Col xs="12">
              <h2 className="small-title">Follow-Up Policy</h2>
            </Col>
            <Col xs="12" md="4">
              <Form.Check
                type="switch"
                id="leadFollowUp.enabled"
                label="Enable follow-up rules"
                checked={Boolean(followUp.enabled)}
                onChange={this.onToggle("enabled")}
              />
            </Col>
            <Col xs="12" md="4">
              <Form.Check
                type="switch"
                id="leadFollowUp.requireOpenLeadFollowUp"
                label="Require open lead follow-up"
                checked={Boolean(followUp.requireOpenLeadFollowUp)}
                onChange={this.onToggle("requireOpenLeadFollowUp")}
              />
            </Col>
            <Col xs="12" md="4">
              <Form.Check
                type="switch"
                id="leadFollowUp.calendarMirror"
                label="Mirror follow-ups to calendar"
                checked={Boolean(followUp.calendarMirror)}
                onChange={this.onToggle("calendarMirror")}
              />
            </Col>

            <Col xs="12" className="mt-4">
              <h2 className="small-title">Consultations</h2>
            </Col>
            <Col xs="12" md="5">
              <Form.Check
                type="switch"
                id="leadFollowUp.consultation.enabled"
                label="Set follow-up after consultation events"
                checked={Boolean(consultation.enabled)}
                onChange={this.onConsultationToggle("enabled")}
              />
            </Col>
            <Col xs="12" md="4">
              <div className="top-label">
                <NumberFormat
                  displayType="input"
                  type="text"
                  className="text-end form-control"
                  thousandSeparator={false}
                  allowNegative={false}
                  value={consultation.daysOut ?? 1}
                  onValueChange={(e) => this.setConsultation("daysOut", e.floatValue)}
                />
                <Form.Label>DAYS AFTER SCHEDULED CONSULTATION</Form.Label>
              </div>
            </Col>
            <Col xs="12" md="3">
              <div className="top-label">
                <Form.Control
                  type="text"
                  value={consultation.eventTypePrefix || "CN"}
                  onChange={(e) => this.setConsultation("eventTypePrefix", e.target.value)}
                />
                <Form.Label>EVENT TYPE PREFIX</Form.Label>
              </div>
            </Col>

            <Col xs="12" className="mt-4">
              <div className="d-flex align-items-center justify-content-between">
                <h2 className="small-title mb-0">Cadence</h2>
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={this.onAddCadence}>
                  Add Rule
                </button>
              </div>
            </Col>
            {(followUp.cadence || []).map(this.renderCadence)}
          </Row>
        </Col>
      </>
    );
  }
}

LeadSettings.propTypes = {
  loading: PropTypes.func.isRequired,
  pageInfo: PropTypes.func.isRequired,
  getSetting: PropTypes.func.isRequired,
  saveSetting: PropTypes.func.isRequired,
  sendAlert: PropTypes.func.isRequired,
};

export default connect(null, {
  loading,
  pageInfo,
  getSetting,
  saveSetting,
  sendAlert,
})(LeadSettings);
